import { ReactNode, useCallback, useEffect, useReducer, useState } from "react";
import { AppNotificationTypeEnum } from "../app-notification-context/AppNotificationContextTypes";
import { useAppNotificationContext } from "../app-notification-context/UseAppNotificationContext";
import { useTranslation } from 'react-i18next';
import { useUserContext } from "../user-context/UserContext";
import settings from "../../app-settings.json";
import signalRHubConfiguration, { SignalRHubEnum } from "../../hooks/use-signalr/SignalRHubInfo";
import { useSignalR } from "../../hooks/use-signalr/UseSignalR";
import { CommandStatusEnum, ILiveDataDeviceStatusModel, ILiveDataStatusModel, LiveDataCommandEnum, LiveDataCommandStateEnum } from "../../services/live-data-service/Models";
import { useOfflineDetectContext } from "../offline-detect-context/UseOfflineDetectContext";
import LiveDataService, { STATUS_NOTIFICATION_TIMEOUT, STATUS_POLLING_INTERVAL } from "../../services/live-data-service/LiveDataService";
import { useRouteContext } from "../../hooks/use-route-context/UseRouteContext";
import i18n from "../../services/localization-service/i18n";
import { ITagValue } from "../../common/models/ITagValue";
import { useConfirmation } from "../confirmation-context/ConfirmationContext";
import { ITagRegisterInfo } from "../../services/properties-data-service/Models";
import { IeLynxObject } from "../../common/models/eLynxObject";
import { eLynxObjectTypeEnum } from "../../common/enums/eLynxObjectType";
import { useInterval } from "../../hooks/use-interval/UseInterval";
import { LiveDataStateItem, LiveDataState, LiveDataAction, LiveDataActionType } from "./LiveDataContextTypes";
import { LiveDataContext } from "./LiveDataContext";

export type LiveDataContextProviderProps = { 
    children: ReactNode; 
};

function getObjectStateItem(items: LiveDataStateItem[], obj: IeLynxObject) {
    const ndx = items.findIndex(o => o.eLynxObject?.id === obj.id && o.eLynxObject?.objectType === obj.objectType);
    return items[ndx];
}

function updateStateItem(stateItem: LiveDataStateItem) {
    const objectName = stateItem.eLynxObject.displayText ?? '';
    switch(stateItem.commandState) {
        case LiveDataCommandStateEnum.InitiatingDemandScan:
        case LiveDataCommandStateEnum.DemandScanInProgress:

            if (stateItem.commandFailed) {
                stateItem.commandState = LiveDataCommandStateEnum.DemandScanFailed;
                stateItem.message = i18n.t('livedata.demandscanFailed' ).replace('{0}', objectName);

            } else {

                stateItem.commandState = LiveDataCommandStateEnum.DemandScanComplete;
                stateItem.message = i18n.t('livedata.demandscanSuccess' ).replace('{0}', objectName);
            }

            break;

        case LiveDataCommandStateEnum.InitiatingWriteDown:
            case LiveDataCommandStateEnum.WriteDownInProgress:

                if (stateItem.commandFailed) {
                    stateItem.commandState = LiveDataCommandStateEnum.WriteDownFailed;
                    stateItem.message = i18n.t('livedata.writeDownFailed' ).replace('{0}', objectName);

                } else {

                    stateItem.commandState = LiveDataCommandStateEnum.WriteDownComplete;
                    stateItem.message = i18n.t('livedata.writeDownSuccess' ).replace('{0}', objectName);
                }

                break;

        case LiveDataCommandStateEnum.CommunicationFailed:
            stateItem.message = i18n.t('livedata.communicationFailed' ).replace('{0}', eLynxObjectTypeEnum[stateItem.eLynxObject.objectType]);
            break;

        case LiveDataCommandStateEnum.CancelingDemandScan:
            stateItem.message = i18n.t('livedata.demandscanCanceled' ).replace('{0}', objectName);
            break;

        case LiveDataCommandStateEnum.CancelingWriteDown:
            stateItem.message = i18n.t('livedata.writedownCanceled' ).replace('{0}', objectName);
            break;


        case LiveDataCommandStateEnum.InitiatingChangeRegister:
        case LiveDataCommandStateEnum.ChangeRegisterInProgress:

            if (stateItem.commandFailed) {
                stateItem.commandState = LiveDataCommandStateEnum.ChangeRegisterFailed;
                stateItem.message = i18n.t('livedata.testChangeRegisterFailed' ).replace('{0}', objectName);

            } else {

                stateItem.commandState = LiveDataCommandStateEnum.ChangeRegisterComplete;
                stateItem.message = i18n.t('livedata.testChangeRegisterSuccess' ).replace('{0}', objectName);
            }

            break;
        

        default:
            break;
    }

}

function liveDataReducer(state: LiveDataState, action: LiveDataAction): LiveDataState {

    let device: ILiveDataDeviceStatusModel | undefined;
    let stateItem: LiveDataStateItem | undefined;

    const newItems = [...state.items];

    switch (action.type) {
        case LiveDataActionType.DemandScan:
        case LiveDataActionType.WriteDown:
        case LiveDataActionType.TestChangeRegister: {
            const newItem: LiveDataStateItem = {
                command: action.type,
                eLynxObject: action.payload.eLynxObject,
                tags: [],
                commandState: action.type === LiveDataActionType.DemandScan ?
                                     LiveDataCommandStateEnum.InitiatingDemandScan :
                                     action.type === LiveDataActionType.WriteDown ?
                                     LiveDataCommandStateEnum.InitiatingWriteDown :
                                     LiveDataCommandStateEnum.InitiatingChangeRegister,
                commandComplete: false,
                commandFailed: false,
                message: action.type === LiveDataActionType.TestChangeRegister ? 
                            i18n.t('livedata.initiatingChangeRegisterTest') as string : 
                            undefined,
                updatedOn: new Date(),
                statusIDs: action.payload.statusIDs
            };

            const items = [...state.items, newItem];

            return {
                ...state,
                items: items
            };
        }

        case LiveDataActionType.GetPollingStatistics: {
            const newItem: LiveDataStateItem = {
                eLynxObject: action.payload.eLynxObject,
                command: action.type,
                tags: [],
                commandState: LiveDataCommandStateEnum.InitiatingGetPollingStatistics,
                commandComplete: false,
                commandFailed: false,
                message: undefined,
                updatedOn: new Date(),
                statusIDs: action.payload.statusIDs
            };

            const items = [...state.items, newItem];

            return {
                ...state,
                items: items
            };
        }

        case LiveDataActionType.CancelCommand:
            
            stateItem = getObjectStateItem(newItems, action.payload.eLynxObject);
            if (stateItem) {
                switch(stateItem.commandState) {
                    case LiveDataCommandStateEnum.InitiatingDemandScan:
                    case LiveDataCommandStateEnum.DemandScanInProgress:
                        stateItem.commandState = LiveDataCommandStateEnum.CancelingDemandScan;

                        break;

                    case LiveDataCommandStateEnum.InitiatingWriteDown:
                    case LiveDataCommandStateEnum.WriteDownInProgress:
                        stateItem.commandState = LiveDataCommandStateEnum.CancelingWriteDown;
                        break;

                    default:
                        break;
                }
            }

            return {
                ...state,
                items: newItems,
            };

        case LiveDataActionType.StatusChanged:
            // make a copy of the current status in state, or a new array if there are none
            device = action.payload.deviceStatusList[0];

            // replace the item or just the status???
            //stateItem = getObjectStateItem(newItems, {
            //    id: device.objectID,
            //    objectType: device.objectType
            //});
            stateItem = newItems.find(o => o.statusIDs?.find(v => v === action.payload.requestID));

            if (stateItem) {
                stateItem.eLynxObject.displayText = device.objectName;

                stateItem.status = action.payload;

                if (action.payload.completedTimestamp) {
                    stateItem.commandComplete = true;

                    stateItem.commandFailed = stateItem.status?.deviceStatusList.flatMap(v => v.statusItems).some(v => v.status === CommandStatusEnum.Failed);

                    updateStateItem(stateItem);

                }
            }

            return {...state,
                items: newItems
            };

        case LiveDataActionType.RemoveStatus: {

            const remove = action.payload ? 
                                state.items.findIndex(o => o.status?.requestID === action.payload.requestID) : 
                                state.items.findIndex(o => o.status === undefined);

            //console.log(`removing livedata status: ${action.payload.id} at index ${remove}`);
            newItems.splice(remove, 1)

            return {
                ...state,
                items: newItems
            };
        }

        case LiveDataActionType.StateItemChanged: {

            updateStateItem(action.payload)

            const stateItems = newItems.filter(o => o.eLynxObject.id !== action.payload.eLynxObject.id && 
                                                        o.eLynxObject.objectType !== action.payload.eLynxObject.objectType);
            stateItems.push(action.payload);
                                                        
            return {...state,
                items: stateItems
            }
        }
    
        default: {
            return state;
        }
    }
}

export function LiveDataContextProvider({ children }: LiveDataContextProviderProps) {

    const [liveDataState, dispatchLiveDataState] = useReducer(liveDataReducer, {
        items: []
    });

    const routeContext = useRouteContext();
    const [, setAppNotificationState] = useAppNotificationContext();
    const [userState] = useUserContext();
    const { t } = useTranslation();
    const [offlineState] = useOfflineDetectContext();
    const [commandInProgress, setCommandInProgress] = useState(false);
    const { acknowledge } = useConfirmation();

    const demandScan = useCallback(async (contextObject: IeLynxObject, writeOnly?: boolean) => {

        setCommandInProgress(true);
        const data = await LiveDataService.demandScan(routeContext.groupID as number, contextObject, undefined, writeOnly);

        dispatchLiveDataState({
            type: LiveDataActionType.DemandScan,
            payload: {
                eLynxObject: contextObject,
                statusIDs: data
            }
        });
    }, [routeContext.groupID, 
        setCommandInProgress
    ]);

    const writeDown = useCallback(async (contextObject: IeLynxObject, values: ITagValue[]) => {
        setCommandInProgress(true);
        const data = await LiveDataService.writeDown(routeContext.groupID as number, contextObject, values);
        dispatchLiveDataState({
            type: LiveDataActionType.WriteDown,
            payload: {
                eLynxObject: contextObject,
                statusIDs: data
            }
        });
    }, [routeContext.groupID, 
        setCommandInProgress
    ]);

    const getPollingStatistics = useCallback(async (pollingServer: string, acmObjectName: string, eLynxObject: IeLynxObject) => {

        setCommandInProgress(true);
        const data = await LiveDataService.getPollingStatistics(routeContext.groupID!, eLynxObject.id, pollingServer, acmObjectName);

        dispatchLiveDataState({
            type: LiveDataActionType.GetPollingStatistics,
            payload: {
                eLynxObject,
                statusIDs: data
            }
        });
    }, [routeContext.groupID, 
        setCommandInProgress
    ]);


    const cancel = useCallback(async (contextObject: IeLynxObject) => {

        await LiveDataService.cancelLiveData(routeContext.groupID as number,
            contextObject);


        dispatchLiveDataState({
            type: LiveDataActionType.CancelCommand,
            payload: {
                eLynxObject: contextObject
            }
        });
    }, [routeContext.groupID]); 

    const testChangeRegister = useCallback(async (registerInfo: ITagRegisterInfo) => {

        setCommandInProgress(true);


        const data = await LiveDataService.testTagRegister(routeContext.groupID as number, registerInfo);

        dispatchLiveDataState({
            type: LiveDataActionType.TestChangeRegister,
            payload: {
                eLynxObject: {
                    id: registerInfo.tagID,
                    objectType: eLynxObjectTypeEnum.Tag
                },
                statusIDs: data
            }
        });
    }, [
        routeContext.groupID, 
        setCommandInProgress
    ]);

    const getLiveStrokes = useCallback(async (contextObject: IeLynxObject) => {

        setCommandInProgress(true);


        const data = await LiveDataService.getLiveStroke(routeContext.groupID!, contextObject.id);

        dispatchLiveDataState({
            type: LiveDataActionType.GetLiveStrokes,
            payload: {
                eLynxObject: {...contextObject},
                statusIDs: data
            }
        });
    }, [
        routeContext.groupID, 
        setCommandInProgress
    ]);    

   
    const getActiveStatus = useCallback((contextObject: IeLynxObject): LiveDataStateItem | undefined => {
        const item = liveDataState.items.find(o => o.eLynxObject.id === contextObject?.id && 
                    o.eLynxObject.objectType === contextObject?.objectType);
        return item;
    }, [liveDataState.items]);

//     // move to utils
//     const updateLiveDataStatus = (status: ILiveDataStatusModel, commandStatus: LiveDataCommandEnum) => {

//         //if (commandInProgress) {

//             console.log('check stale status');
//             // // if live data status signalR notifications do not occur within 5 minutes of the command start, then
//             // // report communication failure.
//             // var expireDate: Date = new Date();
//             // expireDate.setMinutes(expireDate.getMinutes() - 5);

//             // this._deviceCommandMap
//             //     .toEnumerable()
//             //     .where(o => o.value.updatedOn.valueOf() < expireDate.valueOf())
//             //     .select(o => o.value)
//             //     .forEach((item: DevCmdMapItem, index: number): void => {
//             //         this._updateLiveDataNotification(Enums.LiveDataCommandStateEnum.CommunicationFailed, item.id, item.name); 
//             //     });


// //        }

//     };


    const onCheckLiveDataStatus = useCallback(() => {

            //console.log('check stale status');

            // if live data status signalR notifications do not occur within 5 minutes of the command start, then
            // report communication failure.
            const expireDate: Date = new Date();
            expireDate.setMinutes(expireDate.getMinutes() - 5);

            liveDataState.items.filter(o => !o.commandComplete && o.updatedOn && o.updatedOn.valueOf() < expireDate.valueOf())
                .forEach(async (o) => {


                    o.commandState = o.commandState === LiveDataCommandStateEnum.InitiatingChangeRegister ||
                    o.commandState === LiveDataCommandStateEnum.ChangeRegisterInProgress ?
                        LiveDataCommandStateEnum.ChangeRegisterFailed :
                        LiveDataCommandStateEnum.CommunicationFailed;


                    if (!o.statusRetrieved && 
                        o.commandState === LiveDataCommandStateEnum.CommunicationFailed && 
                        (o.statusIDs?.length ?? 0) > 0) {

                        const statusID = (o.statusIDs ?? [])[0];
                        const livedataStatus = await LiveDataService.getStatus(routeContext.groupID as number, statusID);

                        o.statusRetrieved = true;

                        if (livedataStatus) {

                            if (livedataStatus.completedTimestamp) {

                                const deviceStatusItem = livedataStatus.deviceStatusList[0];
                                o.commandState = livedataStatus.command === LiveDataCommandEnum.DemandScan ?
                                                    LiveDataCommandStateEnum.DemandScanInProgress : 
                                                    livedataStatus.command === LiveDataCommandEnum.Write ?
                                                    LiveDataCommandStateEnum.WriteDownInProgress :
                                                    livedataStatus.command === LiveDataCommandEnum.GetPollingStatistics ?
                                                    LiveDataCommandStateEnum.GetPollingStatisticsInProgress :
                                                    o.commandState;
                                o.eLynxObject = {
                                    id: deviceStatusItem.objectID,
                                    objectType: deviceStatusItem.objectType,
                                    objectName: deviceStatusItem.objectName,
                                    displayText: deviceStatusItem.objectName
                                };
                                o.commandComplete = true;
                                o.status = livedataStatus;
                                o.updatedOn = new Date();

                                dispatchLiveDataState({
                                    type: LiveDataActionType.StateItemChanged,
                                    payload: o
                                });

                                return;
                            }
                        }
                    }
                    
                    o.commandComplete = true;
                    o.commandFailed = true;
                    if (o.status) {
                        o.status.completedTimestamp = new Date();

                        o.status.deviceStatusList.forEach(v => {
                            v.deviceStatusMessage = t('liveData.communicationFailed').replace('{0}', v.objectName);
                            v.statusComplete = true;
                        });

                        o.status.deviceStatusList.flatMap(v => v.statusItems).forEach(v => v.status = CommandStatusEnum.Failed);
                    }

                    dispatchLiveDataState({
                        type: LiveDataActionType.StateItemChanged,
                        payload: o
                    });
    
                });

    },[
        liveDataState,
        t,
        routeContext.groupID
    ]);


    const onPollLiveDataStatus = useCallback(() => {

        console.log('polling liveData status');

        
        const statusIDs = liveDataState.items.filter(o => !o.commandComplete).flatMap(o => o.statusIDs ?? []);


        statusIDs.forEach(async (o) => {

            try {

                console.log(`statusID: ${o}`);

                const stateItem = liveDataState.items.find(v => v.statusIDs?.indexOf(o) !== -1);
                if (stateItem && stateItem.commandComplete === false) {

                    const livedataStatus = await LiveDataService.getStatus(routeContext.groupID as number, o);

                    if (livedataStatus) {

                        console.log(`updating state item for status id ${o}`);

                        stateItem.commandState = livedataStatus.command === LiveDataCommandEnum.GetPollingStatistics ?
                            LiveDataCommandStateEnum.GetPollingStatisticsInProgress : 
                        livedataStatus.command === LiveDataCommandEnum.DemandScan ?
                                            LiveDataCommandStateEnum.DemandScanInProgress : 
                                            LiveDataCommandStateEnum.WriteDownInProgress
                        const deviceStatusItem = livedataStatus.deviceStatusList.find(v => v.objectID === stateItem.eLynxObject.id && 
                                                                                        v.objectType === stateItem.eLynxObject.objectType);

                        
                        if (deviceStatusItem) {
                            stateItem.eLynxObject.objectName = deviceStatusItem.objectName;
                            stateItem.eLynxObject.displayText = deviceStatusItem.objectName;
                        }
                        //  stateItem.eLynxObject = {
                        //      id: deviceStatusItem.objectID,
                        //      objectType: deviceStatusItem.objectType,
                        //      objectName: deviceStatusItem.objectName,
                        //      displayText: deviceStatusItem.objectName
                        // };

                        //console.log(`ld status: ${deviceStatusItem.statusComplete} ${livedataStatus.completedTimestamp}`)
                        //o.commandComplete = livedataStatus.true;
                        stateItem.status = livedataStatus;
                        stateItem.updatedOn = new Date();

                        if (livedataStatus.completedTimestamp) {
                            stateItem.commandComplete = true;
                        }
                    } else {
                        console.log(`status does not exist ${o}`);

                        stateItem.updatedOn = new Date();
                       
                        //stateItem.commandComplete = true;
                    }


                    //console.log(`stateItem: ${JSON.stringify(stateItem)}`);

                    dispatchLiveDataState({
                        type: LiveDataActionType.StateItemChanged,
                        payload: stateItem
                    });
                }

            } catch (err) {

                console.log(`onPollLiveDataStatus: ${JSON.stringify(err)}`)
            }
            
        });


        // liveDataState.items.filter(o => !o.commandComplete)
        //     .forEach(async (o) => {

        //         if ((o.statusIDs?.length ?? 0) > 0) {

        //             try {
        //             const statusID = (o.statusIDs ?? [])[0];

        //             console.log(`statusID: ${statusID} ${o.commandComplete}`);

        //             const livedataStatus = await LiveDataService.getStatus(routeContext.groupID as number, statusID);

        //             const deviceStatusItem = livedataStatus.deviceStatusList[0];
        //             o.commandState = livedataStatus.command === LiveDataCommandEnum.DemandScan ?
        //                                  LiveDataCommandStateEnum.DemandScanInProgress : 
        //                                  LiveDataCommandStateEnum.WriteDownInProgress;
        //             o.eLynxObject = {
        //                 id: deviceStatusItem.objectID,
        //                 objectType: deviceStatusItem.objectType,
        //                 objectName: deviceStatusItem.objectName,
        //                 displayText: deviceStatusItem.objectName
        //             };

        //             //console.log(`ld status: ${deviceStatusItem.statusComplete} ${livedataStatus.completedTimestamp}`)
        //             //o.commandComplete = livedataStatus.true;
        //             o.status = livedataStatus;
        //             o.updatedOn = new Date();

        //             dispatchLiveDataState({
        //                 type: LiveDataActionType.StateItemChanged,
        //                 payload: o
        //             });
        //             } catch (err) {

        //                 console.log(`onPollLiveDataStatus: ${JSON.stringify(err)}`)
        //             }
        //         }
                
        //     });
    },[
        liveDataState,
        routeContext.groupID
    ]);


    useInterval(onCheckLiveDataStatus, 
        !commandInProgress || 
        offlineState.offline ? 
            undefined : 
            STATUS_NOTIFICATION_TIMEOUT);


    useInterval(onPollLiveDataStatus,
        !commandInProgress || 
        offlineState.offline ? 
            undefined :
            STATUS_POLLING_INTERVAL
        );


    const onLiveDataCallback = useCallback((value: ILiveDataStatusModel) => {
        // dispatch the state change
        dispatchLiveDataState({
            type: LiveDataActionType.StatusChanged,
            payload: value
        });
    },[dispatchLiveDataState]);

    const hubConfiguration = signalRHubConfiguration.find(o => o.id === SignalRHubEnum.LiveData);
    useSignalR({
        url: `${settings.apiBaseUrl}${hubConfiguration?.path}`,
        customerID: userState.customer?.id,
        userID: userState.user?.id,
        methodName: hubConfiguration?.methodName as string,
        onCallback: onLiveDataCallback,
        offline: offlineState.offline,
        
    });
        

    useEffect(() => {

        liveDataState.items.filter(o => o.commandComplete)
            .forEach(o => {

                window.setTimeout(() => {
                    dispatchLiveDataState({
                        type: LiveDataActionType.RemoveStatus,
                        payload: o.status as ILiveDataStatusModel
                    });
                }, 1000);                    
                // }, 15000);
        
                setCommandInProgress(false);
    
                if (o.command !== LiveDataActionType.GetPollingStatistics) {

                    if (o.commandState !== LiveDataCommandStateEnum.InitiatingChangeRegister && 
                        o.commandState !== LiveDataCommandStateEnum.ChangeRegisterComplete && 
                        o.commandState !== LiveDataCommandStateEnum.ChangeRegisterFailed ) {
                        if (!o.commandFailed) {
                            // display the message only once!
                            if (!o.messageDisplayed) {
                                acknowledge(o.message ?? "");
                                o.messageDisplayed = true;
                            }
                        } else {
                            setAppNotificationState({
                                notificationType: AppNotificationTypeEnum.Error,
                                message: o.message, 
                                show: true,
                            });
                        }
                    }
                }
            });

    }, [liveDataState, 
        acknowledge,
        dispatchLiveDataState,
        setAppNotificationState]);

    return (
        <LiveDataContext.Provider value={[ 
                liveDataState,
                //dispatchLiveDataState, 
                getActiveStatus, 
                demandScan,
                cancel,
                writeDown,
                testChangeRegister,
                getLiveStrokes,
                getPollingStatistics,

                ]}>
            {children}
        </LiveDataContext.Provider>
    );
}


